import {useState} from 'react';

import {useTranslation} from 'react-i18next';

import EditIcon from '@mui/icons-material/Edit';
import {Grid, IconButton, SxProps, Typography} from '@mui/material';

import {CompanyProfile} from '../types';

import {CompanyProfileGeneralInformationForm} from './forms/CompanyProfileGeneralInformationForm';
import {PROFILE_DIALOG_SX, PROFILE_DIALOG_TITLE_SX} from './profileDialogStyles';
import {ProfileSection} from './ProfileSection';
import EmpionDialog from '../../../../components/EmpionDialog/EmpionDialog';

const capitalizedItem: SxProps = {
  textTransform: 'capitalize',
};

type CompanyProfileGeneralInformationProps = {
  company: CompanyProfile;
  updateCompany(data: CompanyProfile): void;
};

const localeName: Record<string, string> = {
  en: 'English',
  de: 'German',
  en_de: 'English & German',
};

export const CompanyProfileGeneralInformation = ({
  company,
  updateCompany,
}: CompanyProfileGeneralInformationProps) => {
  const {t} = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const isLargeCompany = company.employeeCountRange === '250';

  const handleFormSubmit = (data: CompanyProfile) => {
    updateCompany(data);
    toggleModal();
  };

  const handleFormCancel = () => {
    toggleModal();
  };

  return (
    <ProfileSection
      title={t('companyProfile.generalInformation.title')}
      dataTour='profile.general'
      actions={
        <IconButton onClick={toggleModal} data-tour='profile.edit'>
          <EditIcon />
        </IconButton>
      }
    >
      <>
        {company.description?.de ||
        (company.description?.en && company.primaryCompanyLanguage) ||
        company.workMode ||
        company.employeeCountRange ||
        company.companyType ? (
            <Grid container spacing={4}>
              <Grid item container xs={12}>
                {company.description?.de && (
                  <Grid item mb={3}>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.description')}
                    </Typography>
                    <Typography dangerouslySetInnerHTML={{__html: company.description?.de}} />
                  </Grid>
                )}
                {company.description?.en && (
                  <Grid item>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.description')}
                    </Typography>
                    <Typography dangerouslySetInnerHTML={{__html: company.description?.en}} />
                  </Grid>
                )}
              </Grid>
              <Grid item container xs={12}>
                {/* primary language */}
                {company.primaryCompanyLanguage && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.language')}
                    </Typography>
                    <Typography sx={capitalizedItem}>{localeName[company.primaryCompanyLanguage]}</Typography>
                  </Grid>
                )}

                {/* work mode */}
                {company.workMode && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.workMode')}
                    </Typography>
                    <Typography sx={capitalizedItem}>{company.workMode}</Typography>
                  </Grid>
                )}

                {/* employee count */}
                {company.employeeCountRange && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.size')}
                    </Typography>
                    <Typography sx={capitalizedItem}>
                      {isLargeCompany ? `${company.employeeCountRange}+` : company.employeeCountRange}
                    </Typography>
                  </Grid>
                )}

                {/* company type */}
                {company.companyType && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.type')}
                    </Typography>
                    <Typography sx={capitalizedItem}>{company.companyType}</Typography>
                  </Grid>
                )}

                {/* perc. employees are parents */}
                {company.percOfEmployeesAreParents && company.percOfEmployeesAreParents > 0 ? (
                  <Grid item xs={12} sm={6}>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.parents')}
                    </Typography>
                    <Typography sx={capitalizedItem}>{company.percOfEmployeesAreParents || null}%</Typography>
                  </Grid>
                ) : (
                  <></>
                )}

                {/* % women in leadership */}
                {company.percOfWomenInLeadership && company.percOfWomenInLeadership > 0 ? (
                  <Grid item xs={12} sm={6}>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.womenInLeadership')}
                    </Typography>
                    <Typography sx={capitalizedItem}>{company.percOfWomenInLeadership || null}%</Typography>
                  </Grid>
                ) : (
                  <></>
                )}

                {/* Average age */}
                {company.averageEmployeeAge && company.averageEmployeeAge > 0 ? (
                  <Grid item xs={12} sm={6}>
                    <Typography variant='h4' mb={1}>
                      {t('companyProfile.generalInformation.shared.age')}
                    </Typography>
                    <Typography sx={capitalizedItem}>{company.averageEmployeeAge || null}</Typography>
                  </Grid>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid container direction='column' alignItems='center'>
              <Grid item>
                <Typography variant='body1' mt={3} textAlign={'center'} sx={{opacity: 0.3}}>
                  {t('companyProfile.generalInformation.emptyState')}
                </Typography>
              </Grid>
            </Grid>
          )}
      </>
      <EmpionDialog
        open={isModalOpen}
        onClose={toggleModal}
        maxWidth='md'
        showCloseButton
        title={t('companyProfile.generalInformation.dialog.title')}
        sxTitle={PROFILE_DIALOG_TITLE_SX}
        sx={PROFILE_DIALOG_SX}
      >
        <CompanyProfileGeneralInformationForm
          company={company}
          onCancel={handleFormCancel}
          onSubmit={handleFormSubmit}
        />
      </EmpionDialog>
    </ProfileSection>
  );
};
